import { callApi } from "@src/utils/api-client";
import { ISO_DATE_ONLY_PATTERN } from "@src/utils/date-utils";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildPatientDataConfig } from "./configs";
/**
 * Patient Data Service
 * Handles all patient data operations and API communication
 */
export interface PatientDataPayload {
  patient_first_name: string;
  patient_last_name: string;
  patient_dob: string; // YYYY-MM-DD format
  appointment_datetime: string; // ISO 8601 format
  provider_id: number;
  provider_clinic: number; // Department ID
  insurance_member_id: string;
  insurance_package_id: number;
  insurance_policy_subscriber_first_name: string;
  insurance_policy_subscriber_last_name: string;
  insurance_relationship_to_policy_holder: number | string | null;
  insurance_policy_subscriber_dob: string; // YYYY-MM-DD format
  legal_sex?: string;
  service_type?: string;
  patient_email?: string;
  attested_terms_privacy?: boolean;
  consent_to_contact?: boolean;
  guardian?: GuardianPayload;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_theme?: string | null;
  practice_id?: number;
  /** @deprecated Workflow state is assigned only by the API. */
  creation_status?: never;
  attestation_minor?: boolean;
  attestation_adult?: boolean;
  signup_as_minor?: boolean;
  med_mgmt_ind?: number;
  /** @deprecated Workflow failure details are assigned only by the API. */
  error_reason?: never;
}

export interface GuardianPayload {
  first_name: string;
  last_name: string;
  dob: string;
  legal_sex: string;
  gender_identity?: string;
  preferred_pronouns?: string;
  preferred_language?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  phone: string;
  phone_type: string;
  preferred_contact: string;
  ok_to_leave_vm: boolean;
  is_policy_holder?: boolean;
}

export interface PatientDataResponse {
  message?: boolean;
  transactionId?: string;
  error?: string;
}

/**
 * Validation function to check if all required fields are present
 * Only validates fields that are actually required based on CoverCareForm requirements
 * @param payload - The patient data payload
 * @param userUuid - User UUID (required for backend save)
 * @returns Object with isValid flag and error message if invalid
 */
export const validatePatientDataPayload = (
  payload: Partial<PatientDataPayload>,
  userUuid?: string,
) => {
  // Check for user UUID
  if (!userUuid) {
    return {
      isValid: false,
      error: "User UUID is required to save patient data",
    };
  }

  // Check for patient DOB
  if (!payload.patient_dob) {
    return {
      isValid: false,
      error: "Patient date of birth is required",
    };
  }

  // Validate date format for patient DOB if present
  if (payload.patient_dob && !isValidDateFormat(payload.patient_dob)) {
    return {
      isValid: false,
      error: "Invalid patient_dob format. Expected YYYY-MM-DD",
    };
  }

  // Validate other optional date fields if they are present
  if (
    payload.insurance_policy_subscriber_dob &&
    !isValidDateFormat(payload.insurance_policy_subscriber_dob)
  ) {
    return {
      isValid: false,
      error: "Invalid insurance_policy_subscriber_dob format. Expected YYYY-MM-DD",
    };
  }

  // Validate appointment datetime if present
  if (payload.appointment_datetime && !isValidDateTimeFormat(payload.appointment_datetime)) {
    return {
      isValid: false,
      error: "Invalid appointment_datetime format. Expected ISO 8601 (YYYY-MM-DDTHH:mm:ss)",
    };
  }

  return { isValid: true };
};

/**
 * Checks if a string is in valid date format (YYYY-MM-DD)
 */
const isValidDateFormat = (dateString: string): boolean => {
  if (!ISO_DATE_ONLY_PATTERN.test(dateString)) return false;
  const date = new Date(dateString);
  return date instanceof Date && !Number.isNaN(date.getTime());
};

/**
 * Checks if a string is in valid ISO 8601 datetime format
 */
const isValidDateTimeFormat = (dateTimeString: string): boolean => {
  try {
    const date = new Date(dateTimeString);
    return date instanceof Date && !Number.isNaN(date.getTime());
  } catch {
    return false;
  }
};

const sanitizePatientDataForRequest = (
  patientData: Partial<PatientDataPayload>,
): Partial<PatientDataPayload> => {
  const sanitizedPayload = { ...patientData };
  const insurancePackageId = sanitizedPayload.insurance_package_id as unknown;

  if (insurancePackageId && typeof insurancePackageId === "object") {
    delete sanitizedPayload.insurance_package_id;
  }

  // Workflow status is assigned by the API after search/scheduling completes.
  delete (sanitizedPayload as Record<string, unknown>).creation_status;
  delete (sanitizedPayload as Record<string, unknown>).error_reason;

  return sanitizedPayload;
};

// biome-ignore lint/complexity/noStaticOnlyClass: PatientDataService is part of a shared service contract; refactoring to plain functions is out of scope here
export class PatientDataService {
  /**
   * Save patient data to the backend
   * @param userUuid - Unique identifier for the user
   * @param patientData - Patient data payload
   * @returns Response from the API
   */
  static async savePatientData(
    userUuid: string,
    patientData: Partial<PatientDataPayload>,
  ): Promise<PatientDataResponse> {
    try {
      // Validate payload before sending
      const validation = validatePatientDataPayload(patientData, userUuid);
      if (!validation.isValid) {
        ClientLogsService.logError(
          userUuid,
          "patient-data.service.savePatientData",
          "Validation error for patient data payload: API call aborted",
          new Error(validation.error),
          {
            errorType: "CallerError",
            functionName: "savePatientData",
          },
        );
        return { error: validation.error };
      }

      // Include user_uuid in the request body
      const payload = {
        ...sanitizePatientDataForRequest(patientData),
        user_uuid: userUuid,
      };

      const { data, response } = await callApi(buildPatientDataConfig(payload));

      if (!response.ok) {
        const errorData = (data as { message?: string } | null) ?? {};
        const errorMessage =
          errorData.message || `HTTP Error: ${response.status} ${response.statusText}`;

        ClientLogsService.logError(
          userUuid,
          "patient-data.service.savePatientData",
          "API error saving patient data",
          new Error(errorMessage),
          {
            errorType: getFailureTypeForHttpStatus(response.status),
            dependencyName: "PatientDataAPI",
            functionName: "savePatientData",
          },
        );

        return { error: errorMessage };
      }
      const result = data as PatientDataResponse | null;
      ClientLogsService.logWebAction(
        userUuid,
        "patient-data.service.savePatientData",
        "Patient data saved successfully",
        undefined,
        { value: result },
      );
      return result ?? {};
    } catch (error) {
      ClientLogsService.logError(
        userUuid,
        "patient-data.service.savePatientData",
        "Error saving patient data",
        error instanceof Error ? error : new Error("Unknown error"),
        {
          errorType: "DependencyFailure",
          dependencyName: "PatientDataAPI",
          functionName: "savePatientData",
        },
      );
      return {
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}

export default PatientDataService;
